import React, { useState, useEffect, useRef, useCallback, memo } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../../firebase';
import { collection, query, getDocs, orderBy, limit } from 'firebase/firestore';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';

// Scrollbar hiding styles
const autoScrollStyles = `
  /* Hide scrollbars across all browsers */
  .hide-scrollbar {
    -ms-overflow-style: none;
    scrollbar-width: none;
  }
  
  .hide-scrollbar::-webkit-scrollbar {
    display: none;
  }
`;

function CoursesTray() {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [visibleCards, setVisibleCards] = useState(4);
  const containerRef = useRef(null);
  const navigate = useNavigate();
  const { user } = useAuth();
  const [reloadCounter, setReloadCounter] = useState(0);

  useEffect(() => {
    let isMounted = true;
    const fetchTopCourses = async () => {
      if (!user) {
        if (isMounted) setLoading(false);
        return;
      }

      try {
        setError(null);
        const coursesRef = collection(db, 'courses');
        let coursesSnapshot;
        
        try {
          const qWithCreated = query(
            coursesRef,
            orderBy('createdAt', 'desc'),
            limit(20)
          );
          coursesSnapshot = await getDocs(qWithCreated);
        } catch (e) {
          console.warn('Fallback to simple query:', e.message);
          const qFallback = query(coursesRef, limit(20));
          coursesSnapshot = await getDocs(qFallback);
        }

        const coursesData = coursesSnapshot.docs
          .map((doc) => {
            const data = doc.data();
            return {
              id: doc.id,
              title: data.title || 'Untitled Course',
              description: data.description || 'No description available',
              image: data.image || '/images/rcourses.png',
              difficulty: data.modules ? `${data.modules.length} Modules` : 'All Levels',
              duration: data.duration || 'Self-paced',
              targetAudience: data.targetAudience || 'All Learners',
              learningObjectives: data.learningObjectives || [],
              createdAt: data.createdAt || null,
            };
          })
          .sort((a, b) => {
            const aTime = a.createdAt?.toMillis ? a.createdAt.toMillis() : (a.createdAt?.seconds ? a.createdAt.seconds * 1000 : 0);
            const bTime = b.createdAt?.toMillis ? b.createdAt.toMillis() : (b.createdAt?.seconds ? b.createdAt.seconds * 1000 : 0);
            return bTime - aTime;
          })
          .slice(0, 10);

        if (isMounted) {
          setCourses(coursesData);
        }
      } catch (err) {
        console.error('Error fetching courses:', err);
        if (isMounted) setError('Failed to load courses. Please try again later.');
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchTopCourses();
    return () => {
      isMounted = false;
    };
  }, [user, reloadCounter, visibleCards]);

  const handleResize = useCallback(() => {
    if (window.innerWidth < 480) setVisibleCards(1);
    else if (window.innerWidth < 768) setVisibleCards(1.5);
    else if (window.innerWidth < 1024) setVisibleCards(2);
    else if (window.innerWidth < 1440) setVisibleCards(3);
    else setVisibleCards(4);
  }, []);

  useEffect(() => {
    window.addEventListener('resize', handleResize);
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, [handleResize]);

  const scroll = (direction) => {
    const container = containerRef.current;
    if (!container) return;
    const scrollAmount = container.offsetWidth;
    container.scrollBy({
      left: direction === 'left' ? -scrollAmount : scrollAmount,
      behavior: 'smooth',
    });
  };

  const handleCardClick = () => {
    navigate('/courses');
  };

  const handleLearnMore = (courseId, event) => {
    event.stopPropagation();
    navigate('/courses');
  };

  const handleRetry = () => {
    setLoading(true);
    setReloadCounter((c) => c + 1);
  };

  const CourseCard = memo(function CourseCard({ course }) {
    return (
      <div
        key={course.id}
        className="flex-none w-[280px] md:w-[300px] transition-all duration-300 hover:scale-105"
        onClick={() => handleCardClick(course.id)}
        role="button"
        tabIndex={0}
        aria-label={`Open course ${course.title}`}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleCardClick(course.id);
          }
        }}
        style={{ contain: 'layout style paint' }}
      >
        <div className="bg-white rounded-xl shadow-md hover:shadow-xl transition-shadow duration-300 overflow-hidden h-full flex flex-col">
          {/* Image Container */}
          <div className="relative overflow-hidden bg-gradient-to-br from-gray-200 to-gray-300">
            <img
              src={course.image || "/images/rcourses.png"}
              alt={`Course thumbnail for ${course.title}`}
              className="w-full h-44 object-cover transition-transform duration-300 hover:scale-110"
              loading="lazy"
            />
            {/* Difficulty Badge */}
            <div className="absolute top-3 right-3 bg-iof/90 text-white px-3 py-1.5 rounded-full text-xs font-semibold shadow-lg">
              {course.difficulty || "All Levels"}
            </div>
          </div>
          
          {/* Content Container */}
          <div className="p-4 flex-1 flex flex-col">
            <h3 className="font-bold text-base md:text-lg text-gray-900 mb-1 line-clamp-2 leading-tight">
              {course.title}
            </h3>
            
            <p className="text-gray-600 text-sm mb-3 line-clamp-2 flex-1 leading-relaxed">
              {course.description}
            </p>
            
            {/* Footer */}
            <div className="flex items-center justify-between pt-3 border-t border-gray-100">
              <span className="text-xs font-medium text-gray-500 bg-gray-100 px-2.5 py-1 rounded-full">
                {course.duration || "Self-paced"}
              </span>
              <button
                onClick={(e) => handleLearnMore(course.id, e)}
                className="text-sm font-semibold text-iof hover:text-iof/80 transition-colors duration-200 flex items-center gap-1"
              >
                Explore
                <span className="text-lg">→</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  });

  if (loading) {
    const skeletonCount = Math.max(visibleCards, 2);
    return (
      <section className="bg-gradient-to-b from-white to-gray-50 p-4 md:p-6">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
            Popular Learning Paths
          </h2>
          <p className="text-gray-600 text-sm md:text-base mb-6">Discover our most popular courses</p>
          
          <div className="relative">
            <div
              className="overflow-x-hidden flex gap-4 px-2 md:px-0"
            >
              {Array.from({ length: skeletonCount }).map((_, idx) => (
                <div key={idx} className="flex-none w-[280px] md:w-[300px]">
                  <div className="bg-white rounded-xl shadow-md animate-pulse overflow-hidden">
                    <div className="w-full h-44 bg-gradient-to-r from-gray-200 to-gray-300" />
                    <div className="p-4">
                      <div className="h-6 bg-gray-200 rounded w-3/4 mb-3" />
                      <div className="h-4 bg-gray-200 rounded w-full mb-2" />
                      <div className="h-4 bg-gray-200 rounded w-5/6 mb-4" />
                      <div className="flex justify-between items-center pt-2 border-t">
                        <div className="h-6 bg-gray-200 rounded w-20" />
                        <div className="h-6 bg-gray-200 rounded w-16" />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    );
  }

  const totalSlides = Math.max(1, Math.ceil(courses.length / visibleCards));
  const currentSlide = 0;

  return (
    <>
      <style>{autoScrollStyles}</style>
      <section className="bg-gradient-to-b from-white to-gray-50 py-8 md:py-12 px-4 md:px-6">
        <div className="max-w-7xl mx-auto">
          {/* Header Section */}
          <div className="mb-8">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 mb-2">
              Popular Learning Paths
            </h2>
            <p className="text-gray-600 text-sm md:text-base max-w-2xl">
              Explore our most engaging courses and start your learning journey today
            </p>
          </div>

          {/* Error State */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 md:p-6 mb-6">
              <p className="text-red-700 font-medium mb-4">{error}</p>
              <button
                onClick={handleRetry}
                className="px-4 md:px-6 py-2 bg-iof text-white rounded-lg hover:bg-iof/90 font-medium transition-all duration-200"
              >
                Try Again
              </button>
            </div>
          )}

          {/* Courses Container */}
          <div className="relative">
              {/* Left Navigation Button */}
              <button
                onClick={() => scroll('left')}
                className="hidden md:flex absolute left-0 top-1/2 -translate-y-1/2 z-20 bg-white/90 hover:bg-iof hover:text-white text-iof rounded-full p-2 shadow-lg transition-all duration-300 -ml-6"
                aria-label="Scroll left"
              >
                <ChevronLeftIcon className="h-6 w-6" />
              </button>

              {/* Right Navigation Button */}
              <button
                onClick={() => scroll('right')}
                className="hidden md:flex absolute right-0 top-1/2 -translate-y-1/2 z-20 bg-white/90 hover:bg-iof hover:text-white text-iof rounded-full p-2 shadow-lg transition-all duration-300 -mr-6"
                aria-label="Scroll right"
              >
                <ChevronRightIcon className="h-6 w-6" />
              </button>

              {/* Cards Container */}
              <div 
                ref={containerRef}
                className="overflow-x-auto flex gap-4 px-2 md:px-0 py-2 scroll-smooth hide-scrollbar"
                style={{
                  WebkitOverflowScrolling: 'touch',
                  scrollBehavior: 'smooth',
                }}
              >
                {courses.length > 0 ? (
                  courses.map((course) => (
                    <CourseCard key={course.id} course={course} />
                  ))
                ) : null}
              </div>
            </div>
          {/* Progress Dots - Hidden on Mobile, shown on Desktop */}
          {courses.length > 0 && visibleCards >= 2 && (
            <div className="hidden md:flex justify-center mt-6 space-x-2">
              {Array.from({ length: totalSlides }).map((_, index) => (
                <button
                  key={index}
                  className={`h-2 rounded-full transition-all duration-300 ${
                    currentSlide === index 
                      ? 'bg-iof w-6' 
                      : 'bg-gray-300 hover:bg-gray-400 w-2'
                  }`}
                  onClick={() => {
                    const container = containerRef.current;
                    if (!container) return;
                    const newPosition = index * container.offsetWidth;
                    container.scrollTo({ left: newPosition, behavior: 'smooth' });
                  }}
                  aria-label={`Go to slide ${index + 1}`}
                />
              ))}
            </div>
          )}
        </div>
      </section>
    </>
  );
}

export default CoursesTray;